/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setShowTypeNotifications } from 'redux/actions/notificationActions';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import moment from 'moment';

import useApi from 'hooks/useApi';

import { Switch } from 'shared/components/form/Inputs';
import { colors } from 'shared/defaultTheme';
import { Checkbox } from 'shared/components/form/CheckBox';
import { Button } from 'shared/components/Buttons';
import AlertModal from 'shared/components/AlertModal';

import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';

import { useHistory } from 'react-router-dom';
import useNotification from 'hooks/useNotification';
import { selectCurrentClient } from 'redux/actions/clientActions';
import { ShareNotifyContactsWrapper, SearchContacts } from './styles';

const ShareNotifyContacts = ({
  selectedClients,
  setSelectedClients,
  dataForm,
  handleClose,
  onBack,
  onCreate,
}) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { customAreaApi } = useApi();
  const dispatch = useDispatch();

  const { cusNavId, mail } = useSelector((st) => st.auth);

  const [selectedContacts, setSelectedContacts] = useState([]);
  const [allSelectedClients, setAllSelectedClients] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const newData = useRef([]);
  const onToggleError = () => setShowError((currentShow) => !currentShow);
  const { handleSelectNotificationReview, getIndividualById, getMassiveById } = useNotification();

  const [, getContacts] = customAreaApi(
    {
      url: `${window.API_V1}/cli/contacts/${cusNavId}/search/all`,
      method: 'POST',
      data: {
        filters: [
          {
            model: 'Contact',
            field: 'Name',
            asc: true,
          },
        ],
        reduce: {
          limit: 0,
          skip: 0,
        },
      },
    },
    { manual: true }
  );

  const [, createNotification] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const [, createMultipleNotifications] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const removeSelectedContacts = (SelectedMail) => {
    const newContacts = selectedContacts.filter((x) => x.mail !== SelectedMail);
    setSelectedContacts(newContacts);
  };

  const selectedContact = useCallback((e, contact) => {
    const existingContact = selectedContacts.some((x) => x.mail === contact.mail);
    if (!existingContact) {
      const newContacts = [...selectedContacts, contact];
      setSelectedContacts(newContacts);
    } else {
      removeSelectedContacts(contact.mail);
    }
  });

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const {
          data: { data },
        } = await getContacts();
        const filterContacts = data.filter((contact) => contact.mail);
        let newSelectedContacts = [];
        const newSelectClients = selectedClients.map((selectedClient) => {
          const findContacts = filterContacts.filter(
            (contact) => contact.clientId === selectedClient.clientId
          );
          newSelectedContacts = [...newSelectedContacts, ...findContacts];
          return { ...selectedClient, contacts: findContacts };
        });
        setSelectedClients(newSelectClients);
        setSelectedContacts(newSelectedContacts);
        setAllSelectedClients(newSelectClients);
      } catch (error) {
        console.error(`Error Contacts: ${error}`);
      }
    };
    fetchContacts();
  }, []);

  useEffect(() => {
    newData.current = [];
    if (selectedClients.length === 1) {
      newData.current = [
        {
          createdBy: mail,
          name: dataForm.name,
          date: moment(new Date()).toISOString(),
          linkToContact: selectedContacts.map((currentContact) => {
            return { name: currentContact.nameComplete, mail: currentContact.mail, active: true };
          }),
          responsibles: dataForm.responsibles,
          answers: [],
          docs: [],
        },
      ];
    }
    if (selectedClients.length >= 2) {
      newData.current = [
        {
          createdBy: mail,
          name: dataForm.name,
          date: moment(new Date()).toISOString(),
          responsibles: dataForm.responsibles,
          answers: [],
          docs: [],
          clientsAndContacts: selectedClients.map((selectedClient) => {
            const filterContacts = selectedContacts.filter(
              (x) => x.clientId === selectedClient.clientId
            );
            const linkToContacts = filterContacts.map((linkToContact) => {
              return { name: linkToContact.nameComplete, mail: linkToContact.mail, active: true };
            });
            return {
              clientId: selectedClient.clientId,
              clientName: selectedClient.clientName,
              clientI18: selectedClient.clientI18,
              linkToContacts,
            };
          }),
        },
      ];
    }
  }, [selectedContacts]);

  const onSearchContacts = async (e, data) => {
    const { value } = e.target;
    const lowerSearchText = value?.toLowerCase() ?? '';
    let contactResults = [];
    if (lowerSearchText.length > 0) {
      contactResults = data.contacts.filter((contact) => {
        return (
          (contact.name ?? '').toLowerCase().includes(lowerSearchText) ||
          (contact.surname ?? '').toLowerCase().includes(lowerSearchText)
        );
      });
      const newSelectedClients = selectedClients.map((selectedClient) => {
        if (selectedClient.clientId === data.clientId) {
          return { ...selectedClient, contacts: contactResults };
        }
        return { ...selectedClient };
      });
      setSelectedClients(newSelectedClients);
    }
    if (!lowerSearchText.length) {
      setSelectedClients([...allSelectedClients]);
    }
  };

  const onChangeActiveContactAll = (checked, value) => {
    const newSelectClients = selectedClients.map((selectedClient) => {
      if (selectedClient.clientId === value.clientId) {
        return { ...selectedClient, isActiveAll: checked };
      }
      return { ...selectedClient };
    });
    if (checked) {
      const selectedClientsFilter = selectedClients.find((x) => x.clientId === value.clientId);
      setSelectedContacts([...selectedContacts, ...selectedClientsFilter.contacts]);
    } else {
      const filterContacts = selectedContacts.filter((x) => x.clientId !== value.clientId);
      setSelectedContacts(filterContacts);
    }
    setSelectedClients(newSelectClients);
  };

  const onCreateNotification = async () => {
    try {
      let notificationId;
      if (selectedClients.length === 1) {
        const {
          data: { data },
        } = await createNotification({
          url: `${window.API_V1}/cli/notifications/${cusNavId}/${
            selectedClients[0].clientId
          }/${encodeURIComponent(mail)}`,
          data: newData.current,
        });
        [notificationId] = data;
        dispatch(selectCurrentClient(selectedClients[0]));
        dispatch(setShowTypeNotifications(false));
        const currentNotification = await getIndividualById({
          notificationId,
          currentClientId: selectedClients[0].clientId,
        });
        handleSelectNotificationReview(currentNotification);
        history.push(`/notificacion_details`, { isClientNotify: false });
      }

      if (selectedClients.length >= 2) {
        const {
          data: { data },
        } = await createMultipleNotifications({
          url: `${window.API_V1}/gen/not/${cusNavId}/${encodeURIComponent(mail)}`,
          data: newData.current,
        });
        [notificationId] = data;
        dispatch(setShowTypeNotifications(true));
        await onCreate();
        const currentNotification = await getMassiveById(notificationId);
        handleSelectNotificationReview(currentNotification);
        history.push(`/notificacion_details`, { isClientNotify: false });
      }
      handleClose();
    } catch (error) {
      setShowError(true);
      if (error.response?.data.errors && error.response?.data?.errors[0]?.detail === 'CCNO-16') {
        setErrorMessage(t('general_error_notification'));
      } else {
        setErrorMessage(t('custom_field_operation_error_message'));
      }
    }
  };

  const Title = () => {
    return (
      <Row className="row-title">
        <Col sm={12} className="ml-n3">
          <h3>{t('notification_select_clients_contact')}</h3>
        </Col>
      </Row>
    );
  };

  return (
    <ShareNotifyContactsWrapper>
      <Title />
      <div className="w-100 my-3">
        <div className="form-container">
          {selectedClients.map((selectedClient) => (
            <div className="w-100" key={selectedClient.clientId}>
              <div className="w-100 client-name-box mb-3">
                <strong>{selectedClient.clientName}</strong>
              </div>
              <div className="mb-2 d-flex align-items-center">
                <span className="document-select-all-client">
                  {t('documents_select_all_client')}
                </span>
                <Switch
                  className="text-uppercase"
                  id={`switchActiveAll-${selectedClient.clientId}`}
                  color={selectedClient.isActiveAll ? colors.bilbao : colors.error}
                  value={!selectedClient.isActiveAll}
                  onChange={(checked) => {
                    onChangeActiveContactAll(checked, selectedClient);
                  }}
                  classNameMain="ml-1"
                />
              </div>
              {selectedClient.contacts && (
                <div className="contact-box-container mb-4">
                  <div className="mb-3 search-contacts-container">
                    <div className="form search-contacts-form">
                      <SearchContacts
                        Icon={() => null}
                        borderRadius={2}
                        className="mb-4 w-25 m-0 h-50"
                        inputClassName="w-100"
                        buttonClassName=""
                        withButtonClear
                        rightIcon={<SearchIcon fill={colors.black} />}
                        onChange={(e) => onSearchContacts(e, selectedClient)}
                        placeholder={t('client_search_contact')}
                      />
                    </div>
                  </div>
                  <ul className="share-contacts-container">
                    {selectedClient.contacts?.map((contact) => (
                      <li className="mb-3 list-contact share-list-contact" key={contact.mail}>
                        <Checkbox
                          type="checkbox"
                          id="filterCheckAll"
                          label=""
                          checked={selectedContacts.some((file) => file.mail === contact.mail)}
                          onClick={(e) => selectedContact(e, contact, selectedClient)}
                        />
                        <span className="mt-n1">{`${contact.name} ${contact.surname}`}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="d-flex justify-content-end w-100 mt-4">
          <Button
            className="home__btn text-uppercase mr-4"
            type="button"
            label={t('general_back')}
            color="secondary"
            onClick={onBack}
          />
          <Button
            className="home__btn text-uppercase"
            type="button"
            label={t('general_button_send')}
            color="primary"
            onClick={onCreateNotification}
          />
        </div>
      </div>
      <AlertModal
        type="error"
        isOpen={showError}
        toggle={onToggleError}
        message={errorMessage}
        okText={t('general_button_ok')}
        onOk={onToggleError}
      />
    </ShareNotifyContactsWrapper>
  );
};

ShareNotifyContacts.defaultProps = {
  setSelectedClients: null,
  selectedClients: null,
  handleClose: () => null,
  dataForm: null,
  onBack: null,
  onCreate: () => null,
};

ShareNotifyContacts.propTypes = {
  setSelectedClients: PropTypes.func,
  selectedClients: PropTypes.arrayOf(PropTypes.shape({})),
  handleClose: PropTypes.func,
  dataForm: PropTypes.shape({}),
  onBack: PropTypes.func,
  onCreate: PropTypes.func,
};

export default ShareNotifyContacts;
